import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';

import { Loader, AdBlockerPopup, CommunitySlotsModal } from 'components';
import { addMonths, formatSubscriptionDate } from 'utils/dates';
import { PROFILE_ROUTES, ROUTES } from 'configs';
import {
  buildStripeCheckoutPresentmentBody,
  createInvoiceSubscriptionRequest,
  createStripeCheckoutSessionRequest,
} from 'services/subscriptions';
import { getLocation } from 'services/geolocation';
import {
  selectAllSubscriptions,
  selectCurrentUserSubscription,
} from 'store/selectors/subscriptions';
import { getAuthUser } from 'store/selectors/auth';
import { doRefreshCommunities } from 'store/actionCreators/communities';
import { getCurrentUserSubscription } from 'store/actionCreators/subscriptions';
import { getCommunities } from 'store/selectors/communities';
import {
  SubscriptionFrequency,
  SubscriptionPlan,
  SubscriptionStatus,
} from 'types';

import { Content } from '../styled';
import * as Ch from './styled';
import {
  CheckoutBreadcrumb,
  CheckoutBreadcrumbCurrent,
  CheckoutBreadcrumbLink,
  CheckoutBreadcrumbSep,
} from './styled';
import {
  InvoiceDocIcon,
  MastercardIcon,
  SepaIcon,
  StripeIcon,
  StripeWordmark,
  VisaIcon,
} from './PaymentBrandIcons';
import {
  isYupValidationError,
  validateInvoicePayerEmails,
} from './invoicePayerEmailsSchema';

const formatCheckoutAmount = (value: number | string) => {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(n)) return String(value);
  return n.toFixed(2);
};

type SubscriptionInnerProps = {
  planFallback?: SubscriptionPlan | null;
  /** User already completed the community-slots step on Change Plan before checkout. */
  communitySlotsPreResolved?: boolean;
};

const SubscriptionInner = ({
  planFallback = null,
  communitySlotsPreResolved = false,
}: SubscriptionInnerProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { subscriptionId: currentSubscriptionId } = useParams();
  const [searchParams] = useSearchParams();
  const subscriptions = useSelector(selectAllSubscriptions);
  const activeSubscription = useSelector(selectCurrentUserSubscription);
  const profile = useSelector(getAuthUser);
  const communities = useSelector(getCommunities);
  const [hasAdBlocker, setHasAdBlocker] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<'direct' | 'invoice'>(
    'direct',
  );
  const [payerEmailRows, setPayerEmailRows] = useState<string[]>(['']);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);
  const [directCheckoutLoading, setDirectCheckoutLoading] = useState(false);
  const [directCheckoutError, setDirectCheckoutError] = useState<string | null>(
    null,
  );
  const [invoiceResult, setInvoiceResult] = useState<{
    hostedInvoiceUrl: string | null;
    amountLabel?: string | null;
  } | null>(null);
  const [communitySlotsOpen, setCommunitySlotsOpen] = useState(false);
  const autoSlotsPrompted = useRef(false);

  const frequency = searchParams.get('frequency');

  const currentUserSubscriptionFrequency = activeSubscription?.frequency;
  const currentUserSubscriptionStatus = activeSubscription?.status;
  const currentUserSubscription = useMemo(() => {
    if (planFallback && planFallback.id === currentSubscriptionId) {
      return planFallback;
    }
    return subscriptions?.find(({ id }) => id === currentSubscriptionId);
  }, [subscriptions, currentSubscriptionId, planFallback]);

  const currentPlan = currentUserSubscription as SubscriptionPlan;
  const {
    maxGroups,
    maxMembers,
    maxCommunities,
    maxStorage,
    currency,
    monthlyValue,
    yearlyValue,
    name,
    productName,
    tax,
  } = currentPlan;

  const settingsPath = `${ROUTES.PROFILE}/${PROFILE_ROUTES.SETTINGS}`;
  const subscriptionPath = `${ROUTES.PROFILE}/${PROFILE_ROUTES.SUBSCRIPTION}`;
  const displayPlanName = productName || name;

  const isCustom = name.includes('Custom');
  const isCurrent =
    currentUserSubscriptionStatus !== SubscriptionStatus.CANCELLED &&
    currentSubscriptionId === activeSubscription?.subscriptionPlanId &&
    currentUserSubscriptionFrequency === frequency;
  const currentPrice =
    frequency === SubscriptionFrequency.YEAR ? yearlyValue : monthlyValue;

  const displayPayButton = useMemo(() => {
    if (!isCurrent) {
      return true;
    }
    if (
      new Date().getTime() >
        new Date(activeSubscription?.endDate || null).getTime() ||
      (activeSubscription?.status === SubscriptionStatus.ACTIVE &&
        activeSubscription?.planId === SubscriptionStatus.MIGRATED)
    ) {
      return true;
    }
    return false;
  }, [isCurrent, activeSubscription]);

  const ownedCommunitiesList = useMemo(() => {
    if (!profile?.userId) return [];
    return communities
      .filter((c) => c.communityOwner?.userId === profile.userId)
      .map((c) => ({
        id: c.id,
        name: c.name ?? '',
        logo: c.logo ?? null,
        subscriptionSlotActive: c.subscriptionSlotActive,
      }));
  }, [communities, profile?.userId]);

  const activeCommunitiesForSlots =
    activeSubscription?.info?.activeCommunities ??
    activeSubscription?.info?.communities ??
    0;

  useEffect(() => {
    autoSlotsPrompted.current = false;
  }, [currentSubscriptionId]);

  useEffect(() => {
    if (communitySlotsPreResolved) return;
    const max = maxCommunities ?? 0;
    if (!profile?.userId || max <= 0) return;
    if (activeCommunitiesForSlots <= max) return;
    if (!displayPayButton) return;
    if (autoSlotsPrompted.current) return;
    autoSlotsPrompted.current = true;
    setCommunitySlotsOpen(true);
  }, [
    communitySlotsPreResolved,
    activeCommunitiesForSlots,
    maxCommunities,
    displayPayButton,
    profile?.userId,
  ]);

  const expirationDate = useMemo(() => {
    if (displayPayButton) {
      return new Date(Date.now());
    }

    return new Date(activeSubscription.endDate);
  }, [displayPayButton, activeSubscription]);

  const currencySym =
    currency === 'EUR' ? '€' : currency === 'CHF' ? 'CHF ' : '$';

  const yearlySaveVsMonthly = useMemo(
    () => monthlyValue * 12 - yearlyValue,
    [monthlyValue, yearlyValue],
  );

  const renewalDateStr = useMemo(() => {
    const renewal: Date =
      frequency === SubscriptionFrequency.MONTH
        ? displayPayButton
          ? addMonths(expirationDate, 1)
          : expirationDate
        : displayPayButton
        ? addMonths(expirationDate, 12)
        : expirationDate;
    return formatSubscriptionDate(renewal, i18n.language);
  }, [frequency, displayPayButton, expirationDate, i18n.language]);

  const displayedLinePrice = useMemo(() => {
    if (tax > 0) {
      return ((currentPrice * (100 - tax)) / 100).toFixed(2);
    }
    return formatCheckoutAmount(currentPrice);
  }, [currentPrice, tax]);

  const approxMonthlyEq = useMemo(() => {
    if (frequency !== SubscriptionFrequency.YEAR || !yearlyValue) return null;
    return Math.round((yearlyValue / 12) * 100) / 100;
  }, [frequency, yearlyValue]);

  const planEmoji = isCustom ? '🏢' : '🚀';

  const includedItems = useMemo(
    () =>
      (
        [
          'subscriptions.checkout.included1',
          'subscriptions.checkout.included2',
          'subscriptions.checkout.included3',
          'subscriptions.checkout.included4',
          'subscriptions.checkout.included5',
          'subscriptions.checkout.included6',
          'subscriptions.checkout.included7',
          'subscriptions.checkout.included8',
        ] as const
      ).map((key) => t(key)),
    [t],
  );

  const showInvoiceForm =
    displayPayButton &&
    frequency === SubscriptionFrequency.YEAR &&
    !invoiceResult;

  const updatePayerRow = useCallback((index: number, value: string) => {
    setPayerEmailRows((rows) => rows.map((r, j) => (j === index ? value : r)));
  }, []);

  const addPayerRow = useCallback(() => {
    setPayerEmailRows((rows) => [...rows, '']);
  }, []);

  const removePayerRow = useCallback((index: number) => {
    setPayerEmailRows((rows) =>
      rows.length <= 1 ? rows : rows.filter((_, j) => j !== index),
    );
  }, []);

  const submitInvoiceSubscription = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setInvoiceError(null);
      if (!currentSubscriptionId) {
        setInvoiceError(t('subscriptions.checkout.invoiceSubmitError'));
        return;
      }
      let payerEmails: string[];
      try {
        payerEmails = validateInvoicePayerEmails(payerEmailRows, t);
      } catch (err) {
        if (isYupValidationError(err)) {
          setInvoiceError(err.errors[0] ?? err.message);
          return;
        }
        throw err;
      }
      setInvoiceSubmitting(true);
      try {
        const data = await createInvoiceSubscriptionRequest({
          subscriptionPlanId: currentSubscriptionId,
          payerEmails,
        });
        setInvoiceResult({
          hostedInvoiceUrl: data.hostedInvoiceUrl,
          amountLabel: data.amountLabel,
        });
        dispatch(getCurrentUserSubscription.trigger());
      } catch (err) {
        let msg = t('subscriptions.checkout.invoiceSubmitError');
        if (axios.isAxiosError(err)) {
          const fromApi = (err.response?.data as { message?: string })?.message;
          if (typeof fromApi === 'string' && fromApi.length > 0) {
            msg = fromApi;
          }
        }
        setInvoiceError(msg);
      } finally {
        setInvoiceSubmitting(false);
      }
    },
    [currentSubscriptionId, payerEmailRows, dispatch, t],
  );

  const startStripeCheckout = useCallback(async (): Promise<void | false> => {
    setDirectCheckoutError(null);
    if (!currentSubscriptionId) {
      setDirectCheckoutError(t('subscriptions.checkout.stripeCheckoutError'));
      return;
    }
    const freq =
      frequency === SubscriptionFrequency.YEAR ||
      frequency === SubscriptionFrequency.MONTH
        ? frequency
        : null;
    if (!freq) {
      setDirectCheckoutError(t('subscriptions.checkout.stripeCheckoutError'));
      return;
    }
    setDirectCheckoutLoading(true);
    try {
      let geo: { country_code2?: string } | null = null;
      try {
        geo = await getLocation();
      } catch {
        geo = { country_code2: 'CH' };
      }
      const data = await createStripeCheckoutSessionRequest({
        subscriptionPlanId: currentSubscriptionId,
        frequency: freq,
        ...buildStripeCheckoutPresentmentBody(geo),
      });
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      setDirectCheckoutError(t('subscriptions.checkout.stripeCheckoutError'));
    } catch (err) {
      const apiData = axios.isAxiosError(err)
        ? (err.response?.data as
            | { error?: string; message?: string }
            | undefined)
        : undefined;
      if (apiData?.error === 'COMMUNITY_SLOTS_EXCEED_PLAN') {
        setDirectCheckoutError(null);
        setCommunitySlotsOpen(true);
        return false;
      }
      let msg = t('subscriptions.checkout.stripeCheckoutError');
      if (axios.isAxiosError(err)) {
        const fromApi = apiData?.message;
        if (typeof fromApi === 'string' && fromApi.length > 0) {
          msg = fromApi;
        }
      }
      setDirectCheckoutError(msg);
    } finally {
      setDirectCheckoutLoading(false);
    }
  }, [currentSubscriptionId, frequency, t]);

  const onCommunitySlotsClose = useCallback(() => {
    setCommunitySlotsOpen(false);
  }, []);

  const onCommunitySlotsSavedCheckout = useCallback(async () => {
    dispatch(getCurrentUserSubscription.trigger());
    dispatch(doRefreshCommunities.trigger());
    return startStripeCheckout();
  }, [dispatch, startStripeCheckout]);

  const payActionsBlock = useMemo(() => {
    if (!displayPayButton || paymentMethod !== 'direct') return null;
    const amountLabel = `${currencySym}${formatCheckoutAmount(currentPrice)}`;
    return (
      <Ch.PayActions>
        {directCheckoutError ? (
          <Ch.InvoiceFormError role="alert">
            {directCheckoutError}
          </Ch.InvoiceFormError>
        ) : null}
        <Ch.BtnPurchase
          type="button"
          disabled={directCheckoutLoading}
          onClick={() => {
            void startStripeCheckout();
          }}
        >
          {directCheckoutLoading ? (
            <Loader type="button" size="28px" thickness={2} />
          ) : (
            <>
              <span aria-hidden>🔒</span>
              {t('subscriptions.checkout.completePurchase', {
                amount: amountLabel,
              })}
            </>
          )}
        </Ch.BtnPurchase>
      </Ch.PayActions>
    );
  }, [
    displayPayButton,
    paymentMethod,
    currencySym,
    currentPrice,
    directCheckoutLoading,
    directCheckoutError,
    startStripeCheckout,
    t,
  ]);

  return (
    <Content>
      <AdBlockerPopup
        isOpen={hasAdBlocker}
        onClose={() => {
          setHasAdBlocker(false);
        }}
      />
      <Ch.CheckoutBackdrop>
        <Ch.CheckoutPageWrap>
          <CheckoutBreadcrumb aria-label="Breadcrumb">
            <CheckoutBreadcrumbLink to={settingsPath}>
              {t('changePlan.breadcrumbSettings')}
            </CheckoutBreadcrumbLink>
            <CheckoutBreadcrumbSep aria-hidden>›</CheckoutBreadcrumbSep>
            <CheckoutBreadcrumbLink to={subscriptionPath}>
              {t('changePlan.breadcrumbSubscription')}
            </CheckoutBreadcrumbLink>
            <CheckoutBreadcrumbSep aria-hidden>›</CheckoutBreadcrumbSep>
            <CheckoutBreadcrumbCurrent>
              {t('subscriptions.checkout.breadcrumbCheckout', {
                plan: displayPlanName,
              })}
            </CheckoutBreadcrumbCurrent>
          </CheckoutBreadcrumb>

          <Ch.ProgressBar>
            <Ch.ProgStep $doneConnector>
              <Ch.ProgDot $done aria-hidden>
                ✓
              </Ch.ProgDot>
              <Ch.ProgLabel $done>
                {t('subscriptions.checkout.stepPlanSelected')}
              </Ch.ProgLabel>
            </Ch.ProgStep>
            <Ch.ProgStep>
              <Ch.ProgDot $active>2</Ch.ProgDot>
              <Ch.ProgLabel $active>
                {t('subscriptions.checkout.stepOrderSummary')}
              </Ch.ProgLabel>
            </Ch.ProgStep>
            <Ch.ProgStep>
              <Ch.ProgDot>3</Ch.ProgDot>
              <Ch.ProgLabel>
                {t('subscriptions.checkout.stepConfirmation')}
              </Ch.ProgLabel>
            </Ch.ProgStep>
          </Ch.ProgressBar>

          <Ch.CheckoutGrid>
            <div>
              <Ch.OrderCard>
                <Ch.OrderCardTitle>
                  {t('subscriptions.orderSummary')}
                </Ch.OrderCardTitle>
                <Ch.PlanSummaryRow>
                  <Ch.PlanIcon aria-hidden>{planEmoji}</Ch.PlanIcon>
                  <div>
                    <Ch.PlanInfoName>{displayPlanName}</Ch.PlanInfoName>
                    <Ch.PlanInfoSub>
                      {t('subscriptions.checkout.planInfoSub', {
                        billing:
                          frequency === SubscriptionFrequency.YEAR
                            ? t('subscriptions.yearly')
                            : t('subscriptions.monthly'),
                        count: maxCommunities,
                        teams: t('changePlan.specCommunities'),
                      })}
                    </Ch.PlanInfoSub>
                    <Ch.PlanBadge
                      $variant={
                        isCustom ? 'enterprise' : isCurrent ? 'current' : 'new'
                      }
                    >
                      {isCustom
                        ? t('changePlan.tierEnterprise')
                        : isCurrent
                        ? t('changePlan.currentPlanBadge')
                        : t('subscriptions.checkout.badgeNew')}
                    </Ch.PlanBadge>
                  </div>
                </Ch.PlanSummaryRow>

                <Ch.SpecPills>
                  <Ch.SpecPill>
                    <span aria-hidden>🏠</span>
                    <span>
                      <Ch.SpecPillVal>{maxCommunities}</Ch.SpecPillVal>{' '}
                      {t('changePlan.specCommunities')}
                    </span>
                  </Ch.SpecPill>
                  <Ch.SpecPill>
                    <span aria-hidden>👥</span>
                    <span>
                      {t('changePlan.upTo')}{' '}
                      <Ch.SpecPillVal>{maxMembers}</Ch.SpecPillVal>{' '}
                      {t('changePlan.specMembers')}
                    </span>
                  </Ch.SpecPill>
                  <Ch.SpecPill>
                    <span aria-hidden>📋</span>
                    <span>
                      <Ch.SpecPillVal>{maxGroups}</Ch.SpecPillVal>{' '}
                      {t('changePlan.specSchedulers')}
                    </span>
                  </Ch.SpecPill>
                  <Ch.SpecPill>
                    <span aria-hidden>💾</span>
                    <span>
                      <Ch.SpecPillVal>{maxStorage} GB</Ch.SpecPillVal>{' '}
                      {t('changePlan.specStorage')}
                    </span>
                  </Ch.SpecPill>
                </Ch.SpecPills>

                <Ch.PriceBreakdown>
                  <Ch.PbRow>
                    <Ch.PbLabel>
                      {frequency === SubscriptionFrequency.YEAR
                        ? t('subscriptions.checkout.planLineAnnual', {
                            plan: displayPlanName,
                          })
                        : t('subscriptions.checkout.planLineMonthly', {
                            plan: displayPlanName,
                          })}
                    </Ch.PbLabel>
                    <Ch.PbVal>
                      {currencySym}
                      {displayedLinePrice}
                    </Ch.PbVal>
                  </Ch.PbRow>
                  {tax > 0 ? (
                    <Ch.PbRow>
                      <Ch.PbLabel>{t('subscriptions.tax')}</Ch.PbLabel>
                      <Ch.PbVal>{tax}%</Ch.PbVal>
                    </Ch.PbRow>
                  ) : null}
                  {frequency === SubscriptionFrequency.YEAR &&
                  yearlySaveVsMonthly > 0 ? (
                    <Ch.PbRow $highlight>
                      <Ch.PbLabel>
                        {t('subscriptions.checkout.annualSavingLine')}
                      </Ch.PbLabel>
                      <Ch.PbVal>{t('changePlan.saveBadge')}</Ch.PbVal>
                    </Ch.PbRow>
                  ) : null}
                  <Ch.PbDivider />
                  <Ch.PbRowTotal>
                    <Ch.PbLabel>
                      {t('subscriptions.checkout.totalDueToday')}
                    </Ch.PbLabel>
                    <Ch.PbVal>
                      {currencySym}
                      {formatCheckoutAmount(currentPrice)}
                    </Ch.PbVal>
                  </Ch.PbRowTotal>
                </Ch.PriceBreakdown>

                <Ch.IncludedSection>
                  <Ch.IncludedTitle>
                    {t('subscriptions.checkout.includedTitle')}
                  </Ch.IncludedTitle>
                  <Ch.IncludedList>
                    {includedItems.map((line, idx) => (
                      <Ch.IncludedItem key={`included-${idx}`}>
                        <Ch.IncludedCheck aria-hidden>✓</Ch.IncludedCheck>
                        {line}
                      </Ch.IncludedItem>
                    ))}
                  </Ch.IncludedList>
                </Ch.IncludedSection>

                <Ch.InfoBox>
                  <span aria-hidden>ℹ</span>
                  <span>
                    {frequency === SubscriptionFrequency.YEAR
                      ? t('subscriptions.checkout.activationInfoStripe')
                      : t('subscriptions.checkout.activationInfoStripeMonthly')}
                  </span>
                </Ch.InfoBox>
                <Ch.TrustRow>
                  <Ch.TrustItem>
                    <span aria-hidden>🔒</span>
                    {t('subscriptions.checkout.trustSecureStripe')}
                  </Ch.TrustItem>
                  <Ch.TrustItem>
                    <span aria-hidden>↩</span>
                    {t('subscriptions.checkout.trustMoneyBack')}
                  </Ch.TrustItem>
                  <Ch.TrustItem>
                    <span aria-hidden>✕</span>
                    {t('subscriptions.checkout.trustCancel')}
                  </Ch.TrustItem>
                </Ch.TrustRow>
              </Ch.OrderCard>
            </div>

            <Ch.PaymentCard>
              <Ch.PaymentCardTitle>
                {t('subscriptions.checkout.completeOrder')}
              </Ch.PaymentCardTitle>

              <Ch.PaymentMethodSection
                role="radiogroup"
                aria-label={t('subscriptions.checkout.paymentMethodLegend')}
              >
                <Ch.PaymentMethodLegend>
                  {t('subscriptions.checkout.paymentMethodLegend')}
                </Ch.PaymentMethodLegend>
                <Ch.PaymentMethodGrid>
                  <Ch.PaymentMethodCard
                    type="button"
                    $selected={paymentMethod === 'direct'}
                    onClick={() => {
                      setPaymentMethod('direct');
                      setDirectCheckoutError(null);
                    }}
                    aria-checked={paymentMethod === 'direct'}
                    role="radio"
                  >
                    <Ch.PaymentMethodCardTop>
                      <Ch.PaymentMethodRadioUi
                        $checked={paymentMethod === 'direct'}
                      />
                      <Ch.PaymentMethodText>
                        <Ch.PaymentMethodName>
                          {t('subscriptions.checkout.payDirectTitle')}
                        </Ch.PaymentMethodName>
                        <Ch.PaymentMethodDesc>
                          {t('subscriptions.checkout.payDirectDesc')}
                        </Ch.PaymentMethodDesc>
                      </Ch.PaymentMethodText>
                    </Ch.PaymentMethodCardTop>
                    <Ch.PaymentBrandStrip>
                      <Ch.PaymentBrandRow>
                        <VisaIcon />
                        <MastercardIcon />
                        <SepaIcon />
                        <StripeIcon />
                      </Ch.PaymentBrandRow>
                    </Ch.PaymentBrandStrip>
                  </Ch.PaymentMethodCard>
                  <Ch.PaymentMethodCard
                    type="button"
                    $selected={paymentMethod === 'invoice'}
                    onClick={() => {
                      setPaymentMethod('invoice');
                      setDirectCheckoutError(null);
                    }}
                    aria-checked={paymentMethod === 'invoice'}
                    role="radio"
                  >
                    <Ch.PaymentMethodCardTop>
                      <Ch.PaymentMethodRadioUi
                        $checked={paymentMethod === 'invoice'}
                      />
                      <Ch.PaymentMethodText>
                        <Ch.PaymentMethodName>
                          {t('subscriptions.checkout.payInvoiceTitle')}
                        </Ch.PaymentMethodName>
                        <Ch.PaymentMethodDesc>
                          {t('subscriptions.checkout.payInvoiceDesc')}
                        </Ch.PaymentMethodDesc>
                      </Ch.PaymentMethodText>
                    </Ch.PaymentMethodCardTop>
                    <Ch.InvoiceOptionStrip>
                      <InvoiceDocIcon />
                    </Ch.InvoiceOptionStrip>
                  </Ch.PaymentMethodCard>
                </Ch.PaymentMethodGrid>
              </Ch.PaymentMethodSection>

              {paymentMethod === 'direct' ? (
                <>
                  <Ch.PriceDisplay>
                    <Ch.PriceBig>
                      <Ch.PriceBigSup>{currencySym}</Ch.PriceBigSup>
                      {formatCheckoutAmount(currentPrice)}
                      <Ch.PricePeriod>
                        {frequency === SubscriptionFrequency.YEAR
                          ? t('changePlan.perYear')
                          : t('changePlan.perMonth')}
                      </Ch.PricePeriod>
                    </Ch.PriceBig>
                    {approxMonthlyEq != null ? (
                      <Ch.PriceSub>
                        {t('changePlan.approxPerMonth', {
                          price: `${currencySym}${approxMonthlyEq}`,
                        })}
                      </Ch.PriceSub>
                    ) : null}
                    {frequency === SubscriptionFrequency.YEAR &&
                    yearlySaveVsMonthly > 0 ? (
                      <Ch.PriceSave>
                        {t('subscriptions.checkout.priceSaveVsMonthly')}
                      </Ch.PriceSave>
                    ) : null}
                    <Ch.PriceToday>
                      <strong>
                        {t('subscriptions.checkout.chargedToday')} {currencySym}
                        {formatCheckoutAmount(currentPrice)}
                      </strong>
                      <br />
                      {t('subscriptions.checkout.renewsOn', {
                        date: renewalDateStr,
                      })}
                    </Ch.PriceToday>
                  </Ch.PriceDisplay>

                  <Ch.PaySummaryRows>
                    <Ch.PaySummaryRow>
                      <Ch.PaySummaryLabel>
                        {frequency === SubscriptionFrequency.YEAR
                          ? t('subscriptions.checkout.planLineAnnual', {
                              plan: displayPlanName,
                            })
                          : t('subscriptions.checkout.planLineMonthly', {
                              plan: displayPlanName,
                            })}
                      </Ch.PaySummaryLabel>
                      <Ch.PaySummaryVal>
                        {currencySym}
                        {displayedLinePrice}
                      </Ch.PaySummaryVal>
                    </Ch.PaySummaryRow>
                    <Ch.PaySummaryTotalRow>
                      <Ch.PaySummaryLabel>
                        {t('subscriptions.total')}
                      </Ch.PaySummaryLabel>
                      <Ch.PaySummaryVal>
                        {currencySym}
                        {formatCheckoutAmount(currentPrice)}
                      </Ch.PaySummaryVal>
                    </Ch.PaySummaryTotalRow>
                  </Ch.PaySummaryRows>

                  {payActionsBlock}

                  <Ch.StripeNote>
                    <span>
                      {t('subscriptions.checkout.stripeProcessedNote')}
                    </span>
                    <StripeWordmark height={16} />
                  </Ch.StripeNote>

                  <Ch.Guarantee>
                    <Ch.GuaranteeItem>
                      <Ch.GuaranteeIcon aria-hidden>↩</Ch.GuaranteeIcon>
                      {t('subscriptions.checkout.trustMoneyBack')}
                    </Ch.GuaranteeItem>
                    <Ch.GuaranteeItem>
                      <Ch.GuaranteeIcon aria-hidden>✕</Ch.GuaranteeIcon>
                      {t('subscriptions.checkout.trustCancelDetailed')}
                    </Ch.GuaranteeItem>
                    <Ch.GuaranteeItem>
                      <Ch.GuaranteeIcon aria-hidden>🔒</Ch.GuaranteeIcon>
                      {t('subscriptions.checkout.trustSSL')}
                    </Ch.GuaranteeItem>
                  </Ch.Guarantee>
                </>
              ) : (
                <>
                  <Ch.InvoicePriceDisplay>
                    <Ch.InvoicePriceTitle>
                      {t('subscriptions.checkout.invoiceHeadline')}
                    </Ch.InvoicePriceTitle>
                    <Ch.InvoicePriceSub>
                      {t('subscriptions.checkout.invoiceSubline')}
                    </Ch.InvoicePriceSub>
                    {frequency !== SubscriptionFrequency.YEAR ? (
                      <Ch.InvoiceYearlyOnlyBanner role="status">
                        {t('subscriptions.checkout.invoiceYearlyOnly')}
                      </Ch.InvoiceYearlyOnlyBanner>
                    ) : null}
                  </Ch.InvoicePriceDisplay>

                  <Ch.PaySummaryRows>
                    <Ch.PaySummaryRow>
                      <Ch.PaySummaryLabel>
                        {frequency === SubscriptionFrequency.YEAR
                          ? t('subscriptions.checkout.planLineAnnual', {
                              plan: displayPlanName,
                            })
                          : t('subscriptions.checkout.planLineMonthly', {
                              plan: displayPlanName,
                            })}
                      </Ch.PaySummaryLabel>
                      <Ch.PaySummaryVal>
                        {currencySym}
                        {displayedLinePrice}
                      </Ch.PaySummaryVal>
                    </Ch.PaySummaryRow>
                    <Ch.PaySummaryTotalRow>
                      <Ch.PaySummaryLabel>
                        {t('subscriptions.checkout.totalDueToday')}
                      </Ch.PaySummaryLabel>
                      <Ch.PaySummaryVal>
                        {t('subscriptions.checkout.toBeInvoiced')}
                      </Ch.PaySummaryVal>
                    </Ch.PaySummaryTotalRow>
                  </Ch.PaySummaryRows>

                  {frequency === SubscriptionFrequency.YEAR && invoiceResult ? (
                    <Ch.InvoiceSuccessPanel>
                      <Ch.InvoiceSuccessTitle>
                        {t('subscriptions.checkout.invoiceSuccessTitle')}
                      </Ch.InvoiceSuccessTitle>
                      {invoiceResult.amountLabel ? (
                        <Ch.InvoiceSuccessAmount>
                          {t('subscriptions.checkout.invoiceAmount', {
                            amount: invoiceResult.amountLabel,
                          })}
                        </Ch.InvoiceSuccessAmount>
                      ) : null}
                      {invoiceResult.hostedInvoiceUrl ? (
                        <Ch.LinkInvoiceHosted
                          href={invoiceResult.hostedInvoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('subscriptions.checkout.invoiceOpenHosted')}
                        </Ch.LinkInvoiceHosted>
                      ) : (
                        <Ch.InvoiceSuccessNote>
                          {t('subscriptions.checkout.invoiceHostedPending')}
                        </Ch.InvoiceSuccessNote>
                      )}
                    </Ch.InvoiceSuccessPanel>
                  ) : null}

                  {showInvoiceForm ? (
                    <Ch.InvoiceForm
                      onSubmit={submitInvoiceSubscription}
                      noValidate
                    >
                      <Ch.InvoiceFieldLabel htmlFor="payer-email-0">
                        {t('subscriptions.checkout.invoicePayerEmailsLabel')}
                      </Ch.InvoiceFieldLabel>
                      <Ch.InvoiceHint>
                        {t('subscriptions.checkout.invoicePayerEmailsHint')}
                      </Ch.InvoiceHint>
                      {profile?.email ? (
                        <Ch.InvoiceHint>
                          {t('subscriptions.checkout.invoiceBillToProfile', {
                            email: profile.email,
                          })}
                        </Ch.InvoiceHint>
                      ) : null}
                      <Ch.InvoiceEmailList>
                        {payerEmailRows.map((row, i) => (
                          <Ch.InvoiceEmailRow key={`payer-email-${i}`}>
                            <Ch.InvoiceTextInput
                              id={i === 0 ? 'payer-email-0' : undefined}
                              type="email"
                              inputMode="email"
                              autoComplete="email"
                              name={`payerEmails[${i}]`}
                              value={row}
                              onChange={(e) =>
                                updatePayerRow(i, e.target.value)
                              }
                              placeholder={t(
                                'subscriptions.checkout.invoiceEmailPlaceholder',
                              )}
                              aria-invalid={Boolean(invoiceError)}
                            />
                            {payerEmailRows.length > 1 ? (
                              <Ch.BtnInvoiceRowRemove
                                type="button"
                                onClick={() => removePayerRow(i)}
                                aria-label={t(
                                  'subscriptions.checkout.invoiceRemoveEmail',
                                )}
                              >
                                ×
                              </Ch.BtnInvoiceRowRemove>
                            ) : null}
                          </Ch.InvoiceEmailRow>
                        ))}
                      </Ch.InvoiceEmailList>
                      <Ch.BtnInvoiceAddEmail
                        type="button"
                        onClick={addPayerRow}
                      >
                        {t('subscriptions.checkout.invoiceAddEmail')}
                      </Ch.BtnInvoiceAddEmail>
                      {invoiceError ? (
                        <Ch.InvoiceFormError role="alert">
                          {invoiceError}
                        </Ch.InvoiceFormError>
                      ) : null}
                      <Ch.BtnPurchase
                        type="submit"
                        disabled={invoiceSubmitting}
                      >
                        <span aria-hidden>📋</span>
                        {invoiceSubmitting
                          ? t('subscriptions.checkout.invoiceSubmitting')
                          : t('subscriptions.checkout.invoiceSubmit')}
                      </Ch.BtnPurchase>
                    </Ch.InvoiceForm>
                  ) : null}

                  <Ch.StripeNote as="div">
                    {t('subscriptions.checkout.invoicePageNote')}
                  </Ch.StripeNote>

                  <Ch.Guarantee>
                    <Ch.GuaranteeItem>
                      <Ch.GuaranteeIcon aria-hidden>📧</Ch.GuaranteeIcon>
                      {t('subscriptions.checkout.invoiceTrust1')}
                    </Ch.GuaranteeItem>
                    <Ch.GuaranteeItem>
                      <Ch.GuaranteeIcon aria-hidden>🏦</Ch.GuaranteeIcon>
                      {t('subscriptions.checkout.invoiceTrust2')}
                    </Ch.GuaranteeItem>
                  </Ch.Guarantee>
                </>
              )}
            </Ch.PaymentCard>
          </Ch.CheckoutGrid>
        </Ch.CheckoutPageWrap>
      </Ch.CheckoutBackdrop>
      <CommunitySlotsModal
        isOpen={communitySlotsOpen}
        onClose={onCommunitySlotsClose}
        maxSlots={Math.max(0, maxCommunities ?? 0)}
        userId={profile?.userId ?? ''}
        communities={ownedCommunitiesList}
        onSaved={onCommunitySlotsSavedCheckout}
      />
    </Content>
  );
};

export default SubscriptionInner;
